import React from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';

import { IconArrowHead, NamedLink } from '../../components';

import css from './BackButton.module.css';

/**
 * A back arrow in a round bubble.
 *
 * Two ways of going back, because "back" means different things:
 *
 * - `to`: a fixed destination. For a step that has one obvious parent
 *   whatever the route there was, like checkout and its listing. History is
 *   no guide there: a buyer often reaches checkout through login or signup,
 *   and one step back would land on the login page. Renders a link.
 *
 * - no `to`: wherever the visitor came from inside the app (search results,
 *   a profile, favourites), with their scroll position. If the page was opened
 *   directly, from a shared link or a search engine, there is nothing of ours
 *   behind it, and the button goes to `fallback` instead. React Router gives
 *   every in-app location a `key`; the entry a tab was opened on has none.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {Object} [props.to] - `{ name, params }` of a route to link to
 * @param {Object} [props.fallback] - `{ name, params }`, used without `to`
 *   when there is no in-app history. Defaults to the search page.
 * @param {string} [props.ariaLabel] - defaults to "Tilbake"
 * @returns {JSX.Element}
 */
const BackButton = props => {
  const { className, rootClassName, to, fallback = { name: 'SearchPage' }, ariaLabel } = props;
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const classes = classNames(rootClassName || css.root, className);
  const label = ariaLabel || intl.formatMessage({ id: 'BackButton.ariaLabel' });
  const icon = <IconArrowHead direction="left" size="big" rootClassName={css.icon} />;

  if (to) {
    return (
      <NamedLink className={classes} name={to.name} params={to.params} ariaLabel={label}>
        {icon}
      </NamedLink>
    );
  }

  const handleClick = () => {
    if (history.location.key) {
      history.goBack();
    } else {
      history.push(
        createResourceLocatorString(fallback.name, routeConfiguration, fallback.params || {}, {})
      );
    }
  };

  return (
    <button type="button" className={classes} onClick={handleClick} aria-label={label}>
      {icon}
    </button>
  );
};

export default BackButton;
